import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import { useCallback, useEffect, useMemo, useState } from "react";
import { del, get } from "../../../../utils/helpers/api";
import { useNavigate } from "react-router-dom";
import IconButton from "../../../../components/Buttons/IconButton";
import { IoCreateOutline } from "react-icons/io5";
import { BsTrash3 } from "react-icons/bs";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import DefaultPagination from "../../../../components/Pagination";
import ConfirmationModal from "../../../../components/Modals/Confirmation";
import { useTranslation } from "react-i18next";
import AgGridTable from "../../../../components/Tables/AgGrid";

function ActionsCellRenderer({ data, context }) {
  return (
    <div className="d-flex align-items-center gap-2 h-100">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.navigate(`/settings/branches/edit/${data.id}`);
        }}
        size="sm" outline color="secondary"
      >
        <IoCreateOutline />
      </IconButton>
      {/* The default branch anchors document numbering and every backfill, so it
          has no delete affordance rather than a button that always fails. */}
      {!data.is_default && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            context.setBranchToDelete(data.id);
          }}
          size="sm" outline color="danger"
        >
          <BsTrash3 />
        </IconButton>
      )}
    </div>
  );
}

function BranchesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [branchToDelete, setBranchToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const getBranches = useCallback(async () => {
    setLoading(true);
    const data = await get(`/branches?page=${currentPage}&page_size=${pageSize}`);
    if (data?.success) {
      setRowData(data.branches.data);
      setTotalPages(data.branches.last_page);
      setTotalRecords(data.branches.total);
    }
    setLoading(false);
  }, [currentPage, pageSize]);

  useEffect(() => {
    getBranches();
  }, [getBranches]);

  const deleteBranch = async () => {
    setLoading(true);
    const data = await del(`/branches/delete/${branchToDelete}`);
    if (data?.success) {
      getBranches();
    } else {
      setDeleteError(data?.message ?? t('common.somethingWentWrong'));
    }
    setBranchToDelete(null);
    setLoading(false);
  };

  const columnDefs = useMemo(() => [
    { field: 'code', headerName: t('common.code'), flex: 1, sortable: true },
    { field: 'name', headerName: t('common.name'), flex: 2, sortable: true },
    { field: 'city', headerName: t('address.city'), flex: 1, sortable: true },
    { field: 'warehouses_count', headerName: t('warehouses.title'), flex: 1, sortable: true },
    {
      field: 'is_default',
      headerName: t('branches.isDefault'),
      flex: 1,
      sortable: true,
      cellRenderer: (params) => params.value ? t('common.yes') : t('common.no')
    },
    {
      field: 'is_active',
      headerName: t('common.active'),
      flex: 1,
      sortable: true,
      cellRenderer: (params) => params.value ? t('common.yes') : t('common.no')
    },
    { headerName: t('common.actions'), minWidth: 100, flex: 1, sortable: false, resizable: false, cellRenderer: ActionsCellRenderer }
  ], [t]);

  const defaultColDef = useMemo(() => ({ resizable: true }), []);
  const gridContext = useMemo(() => ({ navigate, setBranchToDelete }), [navigate]);

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('branches.title')} onReload={getBranches} dataTotal={totalRecords}>
          <DefaultButton color="primary" size="sm" onClick={() => navigate('/settings/branches/create')}>
            {t('branches.newBranch')}
          </DefaultButton>
        </PageSubHeader>

        <div className="subheader-page-content">
          <AgGridTable
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            context={gridContext}
          />

          <DefaultPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>

      <ConfirmationModal
        isOpen={branchToDelete !== null}
        title={t('branches.deleteBranch')}
        message={t('branches.deleteBranchConfirm')}
        onConfirm={deleteBranch}
        toggle={() => setBranchToDelete(null)}
      />

      <ConfirmationModal
        isOpen={deleteError !== null}
        title={t('branches.deleteBranch')}
        message={deleteError ?? ''}
        onConfirm={() => setDeleteError(null)}
        toggle={() => setDeleteError(null)}
      />
    </SidebarLayout>
  );
}

export default BranchesPage;
